import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, User, ArrowLeft, ChevronLeft } from 'lucide-react';
import { AnimatedSection } from '../components/shared/AnimatedSection';
import { blogPosts } from '../data/blogPosts';

function BlogDetail() {
  const { slug } = useParams<{ slug: string }>();
  const post = blogPosts.find(p => p.slug === slug);

  // 滚动到顶部
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!post) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="text-center px-4">
          <h1 className="text-4xl font-bold text-neutral-900 mb-4">Artículo no encontrado</h1>
          <p className="text-neutral-500 mb-8">El artículo que buscas no existe o ha sido eliminado.</p>
          <Link 
            to="/blog"
            className="inline-flex items-center gap-2 bg-neutral-900 text-white px-6 py-3 rounded-full font-medium hover:bg-neutral-800 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Volver al blog
          </Link>
        </div>
      </div>
    );
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero区域 */}
      <section className="relative h-[60vh] min-h-[500px] flex items-center justify-center overflow-hidden">
        {/* 背景图 */}
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: `url("${post.image}")`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div className="absolute inset-0 bg-black/60" />
        </div>
        
        {/* 内容 - 居中 */}
        <div className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto">
          <AnimatedSection>
            {/* 返回按钮 */}
            <Link 
              to="/blog" 
              className="inline-flex items-center gap-1.5 text-white/80 hover:text-white mb-6 text-sm font-medium transition-colors"
            >
              <ChevronLeft className="h-4 w-4" />
              Blog
            </Link>

            {/* 分类 */}
            <div className="mb-6">
              <span className="inline-block bg-white text-neutral-900 px-4 py-1.5 rounded-full text-sm font-semibold">
                {post.category}
              </span>
            </div>

            {/* 标题 */}
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
              {post.title}
            </h1>

            {/* 元信息 */}
            <div className="flex flex-wrap items-center justify-center gap-4 text-white/70 text-sm">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                <span>{formatDate(post.publishDate)}</span>
              </div>
              <span className="text-white/40">•</span>
              <div className="flex items-center gap-2">
                <User className="h-4 w-4" />
                <span>{post.author}</span>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* 文章内容 */}
      <article className="py-12 md:py-20">
        <div className="container-max px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <AnimatedSection>
              {/* 摘要 */}
              <div className="mb-12">
                <p className="text-xl md:text-2xl text-neutral-600 leading-relaxed font-light">
                  {post.excerpt}
                </p>
              </div>

              {/* 分隔线 */}
              <div className="w-16 h-px bg-neutral-200 mb-12" />

              {/* 正文 */}
              <div 
                className="prose prose-lg prose-neutral max-w-none
                  prose-headings:font-bold prose-headings:text-neutral-900
                  prose-h2:text-2xl prose-h2:mt-12 prose-h2:mb-6
                  prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-4
                  prose-p:text-neutral-600 prose-p:leading-relaxed prose-p:mb-6
                  prose-a:text-neutral-900 prose-a:font-medium prose-a:underline prose-a:underline-offset-4 hover:prose-a:text-neutral-600
                  prose-strong:text-neutral-900 prose-strong:font-semibold
                  prose-ul:my-6 prose-ul:list-disc prose-ul:pl-6
                  prose-ol:my-6 prose-ol:list-decimal prose-ol:pl-6
                  prose-li:text-neutral-600 prose-li:mb-2 prose-li:pl-2
                "
                dangerouslySetInnerHTML={{ __html: post.fullContent }}
              />

              {/* 文章结尾 */}
              <div className="mt-16 pt-8 border-t border-neutral-200">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
                  {/* 作者信息 */}
                  <div className="flex items-center gap-4">
                    <img 
                      src="/cropped-Logo-negro-Zwheel_ok.png" 
                      alt="ZWheel" 
                      className="h-10 w-auto"
                    />
                    <div>
                      <p className="font-semibold text-neutral-900">{post.author}</p>
                      <p className="text-sm text-neutral-500">{formatDate(post.publishDate)}</p>
                    </div>
                  </div>

                  {/* 返回按钮 */}
                  <Link 
                    to="/blog"
                    className="inline-flex items-center gap-2 text-neutral-600 hover:text-neutral-900 font-medium transition-colors"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Volver al blog
                  </Link>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </article>
    </div>
  );
}

export default BlogDetail;
